import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DialogFooter } from '@/components/ui/dialog'
import { useSecondsUntil } from '@/hooks/useSecondsUntil'
import {
  AUTOMATION_STATUS_LABELS,
  AutomationStatus,
  type OutboundCallAutomation,
  useCancelAutomation,
  useDismissAutomation,
  usePauseAutomation,
  useStartAutomation,
} from '@/models/outboundCallAutomation'

interface OutboundCallAutomationManageProps {
  automation: OutboundCallAutomation
  onDone: () => void
}

function Stat({ label, value, onClick }: { label: string; value: number; onClick?: () => void }) {
  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={!onClick}
      className="bg-surface flex h-auto flex-col items-start gap-0 p-3 text-left disabled:cursor-default disabled:opacity-100"
    >
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-muted-foreground text-xs">{label}</div>
    </Button>
  )
}

export function OutboundCallAutomationManage({ automation, onDone }: OutboundCallAutomationManageProps) {
  const start = useStartAutomation()
  const pause = usePauseAutomation()
  const cancel = useCancelAutomation()
  const dismiss = useDismissAutomation()
  const counts = automation.leadCounts
  const paused = automation.status === AutomationStatus.PAUSED
  const completed = automation.status === AutomationStatus.COMPLETED
  const autoPausedSecs = useSecondsUntil(automation.autoPausedUntil)
  const [, setSearchParams] = useSearchParams()

  // Filter the leads list to this automation's leads in the given status, then close.
  function filterByTag(tagId: string | null | undefined) {
    if (!tagId) {
      toast.error('No leads in this status yet')
      return
    }
    setSearchParams({ tags: tagId })
    onDone()
  }

  async function run(action: typeof start | typeof pause | typeof cancel, label: string, done = false) {
    try {
      await action.mutateAsync(automation.id)
      if (done) onDone()
    } catch {
      toast.error(`Failed to ${label} automation`)
    }
  }

  // Dismiss keeps the dialog open: clearing the summary flips the active query
  // to null, so the create form takes over and a new automation can be started.
  async function runDismiss() {
    try {
      await dismiss.mutateAsync(automation.id)
    } catch {
      toast.error('Failed to dismiss automation')
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <span className="font-medium">{automation.name || 'Automation'}</span>
        <Badge variant="secondary">{AUTOMATION_STATUS_LABELS[automation.status]}</Badge>
        {autoPausedSecs !== null && <Badge variant="secondary">Paused — resuming in {autoPausedSecs}s</Badge>}
      </div>
      {automation.pauseAfterLeads && automation.pauseDurationSeconds ? (
        <p className="text-muted-foreground text-sm">
          Auto-pauses {automation.pauseDurationSeconds}s after every {automation.pauseAfterLeads} calls.
        </p>
      ) : null}

      <ul className="text-muted-foreground list-inside list-disc text-sm">
        {automation.filterSummary.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      {completed ? (
        // Final run summary: total + terminal dispositions. Stats with a status
        // tag are clickable to filter the leads list.
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Total" value={counts.total} />
            <Stat
              label="Processed"
              value={counts.done}
              onClick={() => filterByTag(automation.statusTags.done)}
            />
            <Stat
              label="Skipped"
              value={counts.skipped}
              onClick={() => filterByTag(automation.statusTags.skipped)}
            />
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Transferred" value={counts.transferred} />
            <Stat label="No answer" value={counts.noAnswer} />
            <Stat label="Dial failed" value={counts.failed} />
          </div>
        </>
      ) : (
        // Total + in-flight states up top; terminal dispositions below. Stats with
        // a status tag are clickable to filter the leads list; the call-outcome
        // breakdown (transferred / no answer) is read-only.
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Total" value={counts.total} />
            <Stat
              label="Pending"
              value={counts.notStarted}
              onClick={() => filterByTag(automation.statusTags.notStarted)}
            />
            <Stat
              label="In progress"
              value={counts.processing}
              onClick={() => filterByTag(automation.statusTags.processing)}
            />
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat
              label="Skipped"
              value={counts.skipped}
              onClick={() => filterByTag(automation.statusTags.skipped)}
            />
            <Stat label="Transferred" value={counts.transferred} />
            <Stat label="No answer" value={counts.noAnswer} />
            {counts.failed > 0 && <Stat label="Dial failed" value={counts.failed} />}
          </div>
        </>
      )}

      {completed ? (
        <DialogFooter>
          <Button onClick={runDismiss} loading={dismiss.isPending ? 'Dismissing…' : undefined}>
            Dismiss
          </Button>
        </DialogFooter>
      ) : (
        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            className="text-red-600 hover:text-red-700"
            onClick={() => run(cancel, 'end', true)}
            loading={cancel.isPending ? 'Ending…' : undefined}
          >
            End automation
          </Button>
          {automation.status === AutomationStatus.RUNNING ? (
            <Button
              variant="outline"
              onClick={() => run(pause, 'pause')}
              loading={pause.isPending ? 'Pausing…' : undefined}
            >
              Pause
            </Button>
          ) : (
            <Button
              onClick={() => run(start, paused ? 'resume' : 'start')}
              loading={start.isPending ? (paused ? 'Resuming…' : 'Starting…') : undefined}
            >
              {paused ? 'Resume' : 'Start'}
            </Button>
          )}
        </DialogFooter>
      )}
    </div>
  )
}
